import React from "react";
import SensitiveInfo, { SensitiveInfoUpdate } from "./SensitiveInfo";
import { writeFieldToForm } from "./formFieldHelpers";
import FormConstants from "./DataEntryFieldsConstants";
import {
    SensitiveInfo as SensitiveInfoModel,
    SensitiveInfoRelease,
    SensitiveInfoType,
    SensitiveInfoUpdateAPI,
} from "../../../models/transaction";
import { FieldGroupRow, FieldGroupRowStatus } from "../../../models/intake";

export interface SensitiveInfoFieldProps {
    /** props.transactionDetails.sensitiveInfo - same source both classic and Quick Entry read. */
    readonly data: readonly SensitiveInfoModel[];
    readonly transactionId?: string;
    readonly dispatch: Function;
    readonly formName?: string;
    readonly errorMsg?: string;
    /** Hides the field entirely - classic pane's isVisibleSensitiveInfoToRelease gate. */
    readonly visible: boolean;
    readonly saveAndUpdateSensitiveInfo?: (data: SensitiveInfoUpdateAPI) => void;
}

/**
 * Shared Sensitive Information field (Spec #34098 / F1a). Wraps the existing
 * SensitiveInfo.tsx checkbox grid. Unlike RecordsRequestedField this field
 * has no component-local state of its own - every update is derived
 * directly from props.data (transactionDetails.sensitiveInfo) and persisted
 * immediately via saveAndUpdateSensitiveInfo, the exact pattern
 * DataEntryDetailsRightPane.tsx's own saveSensitiveInfo used inline before
 * this extraction. Both classic RightPane and Quick Entry mount this same
 * component now.
 */
const SensitiveInfoField: React.FC<SensitiveInfoFieldProps> = (props) => {
    if (!props.visible) {
        return null;
    }

    const formName = props.formName ?? FormConstants.DATA_ENTRY_FIELDS_FORM;

    const onChange = (update: readonly SensitiveInfoUpdate[]) => {
        applySensitiveInfoUpdate(props.data, update, props.transactionId, props.dispatch, props.saveAndUpdateSensitiveInfo, formName);
    };

    return (
        <SensitiveInfo
            data={props.data}
            onChange={onChange}
            errorMsg={props.errorMsg}
        />
    );
};

export default SensitiveInfoField;

export const SENSITIVE_INFO_ROW_ID = "sensitiveInfo";

/** Applies a set of checkbox changes: resolved sync-error flags + immediate API persistence. */
export function buildUpdatedSensitiveInfo(data: readonly SensitiveInfoModel[], update: readonly SensitiveInfoUpdate[]): readonly SensitiveInfoModel[] {
    return data.map(item => {
        const newItem = update.find(upd => upd.updatedItemId === item.id);
        return newItem ? {
            ...item,
            stateID: newItem.checked ? SensitiveInfoRelease.Authorized : SensitiveInfoRelease.Prohibited,
        } :
            item.stateID === SensitiveInfoRelease.NotMentioned ? { ...item, stateID: SensitiveInfoRelease.Prohibited } :
                item;
    });
}

function buildSensitiveInfoUpdatePayload(
    data: readonly SensitiveInfoModel[],
    update: readonly SensitiveInfoUpdate[],
    transactionId: string | undefined,
): SensitiveInfoUpdateAPI {
    const updatedValues = buildUpdatedSensitiveInfo(data, update);
    const genetic = updatedValues.find(item => item.id === SensitiveInfoType.GeneticInformation)?.stateID;
    const hiv = updatedValues.find(item => item.id === SensitiveInfoType.HivAids)?.stateID;
    const mhlt = updatedValues.find(item => item.id === SensitiveInfoType.MentalHealth)?.stateID;
    const drug = updatedValues.find(item => item.id === SensitiveInfoType.SubstanceAbuse)?.stateID;
    const std = updatedValues.find(item => item.id === SensitiveInfoType.Std)?.stateID;

    const sensitiveInfoEntries = data.map(item => {
        const updatedRecord = update.find(upd => upd.updatedItemId === item.id);
        return {
            model: "sensitiveInfoItem",
            id: item.id,
            name: item.name,
            stateID: updatedRecord
                ? (updatedRecord.checked ? SensitiveInfoRelease.Authorized : SensitiveInfoRelease.Prohibited)
                : (item.stateID === SensitiveInfoRelease.NotMentioned ? SensitiveInfoRelease.Prohibited : item.stateID),
        };
    });

    return {
        UpdatePayload: {
            type: "",
            data: {
                transactionId: transactionId ?? "",
                userID: global.userID,
                geneticSensitiveInfo: genetic,
                hivAidsSensitiveInfo: hiv,
                mentalHealthSensitiveInfo: mhlt,
                substanceAbuseSensitiveInfo: drug,
                sexuallyTransmittedDiseaseSensitiveInfo: std,
            },
            update: false,
        },
        InfoList: sensitiveInfoEntries,
    } as SensitiveInfoUpdateAPI;
}

/**
 * Shared entry point for "apply a batch of Sensitive Info checkbox changes":
 * writes the DISCLOSE_ALL_INFO/DONT_DISCLOSE_ANY_INFO derived flags into the
 * form and persists via saveAndUpdateSensitiveInfo. Used by this field's own
 * onChange AND by DataEntryDetailsRightPane.tsx's onChangeRequestType (the
 * AUDIT request-type auto-authorize-all cascade) - both need the exact same
 * derived-payload logic, not just the checkbox-grid UI.
 */
export function applySensitiveInfoUpdate(
    data: readonly SensitiveInfoModel[],
    update: readonly SensitiveInfoUpdate[],
    transactionId: string | undefined,
    dispatch: Function,
    saveAndUpdateSensitiveInfo: ((data: SensitiveInfoUpdateAPI) => void) | undefined,
    formName: string = FormConstants.DATA_ENTRY_FIELDS_FORM,
): void {
    const updatedValues = buildUpdatedSensitiveInfo(data, update);

    writeFieldToForm(dispatch, FormConstants.DISCLOSE_ALL_INFO, updatedValues.every(item =>
        item.stateID === SensitiveInfoRelease.Authorized), formName);
    writeFieldToForm(dispatch, FormConstants.DONT_DISCLOSE_ANY_INFO, updatedValues.every(item =>
        item.stateID === SensitiveInfoRelease.Prohibited), formName);

    saveAndUpdateSensitiveInfo?.(buildSensitiveInfoUpdatePayload(data, update, transactionId));
}

/**
 * Builds the FieldGroupRow for the Sensitive Information row. Confirmed once
 * every item has been explicitly authorized or denied (no NotMentioned left)
 * - mirrors isSensitiveInfoSelected's own "every item resolved" requirement
 * (validations/localValidations.ts); NeedsEntry otherwise.
 */
export function mapSensitiveInfoToFieldGroupRow(badgeNumber: number, data: readonly SensitiveInfoModel[]): FieldGroupRow {
    const hasUnresolved = data.some(item => item.stateID === SensitiveInfoRelease.NotMentioned);
    const isResolved = data.length > 0 && !hasUnresolved;
    const authorizedCount = data.filter(item => item.stateID === SensitiveInfoRelease.Authorized).length;

    let value: string | null = null;
    if (isResolved) {
        value = authorizedCount === 0 ? "None authorized"
            : authorizedCount === data.length ? "All authorized"
                : `${authorizedCount} of ${data.length} authorized`;
    }

    return {
        id: SENSITIVE_INFO_ROW_ID,
        badgeNumber,
        status: isResolved ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.NeedsEntry,
        label: "Sensitive Information",
        value,
        isAiPreSelected: false,
    };
}
